import { Button } from "@/ui/shadcn/button";
import { useState, useEffect, useRef, useMemo } from "preact/hooks";
import { getL11nString } from "@/l11n/l11n";
import { uiLangIsRtl } from "@/l11n/l11n";
import { localStorageGet, STORAGE_KEYS } from "@/lib/storage";
import { doLog } from "@/lib/globals";
import { isDesktop } from "@/lib/web";

export default function MessageInput({
  onSubmit,
  onFocus,
  onBlur,
  isVisible,
  placeholder,
  // eslint-disable-next-line no-unused-vars
  emphasize,
}) {
  const [input, setInput] = useState("");
  const [typingStartTime, setTypingStartTime] = useState(null);
  const inActivity = localStorageGet(STORAGE_KEYS.inActivity);
  const inputRef = useRef(null);
  const hasMounted = useRef(false);

  const strings = useMemo(
    () => ({
      placeholder: getL11nString("default_input_placeholder"),
      submit: getL11nString("submit"),
    }),
    []
  );

  // For emphasis animation
  // const [visible, setVisible] = useState(true);
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     setVisible((prev) => !prev);
  //   }, 750);
  //   return () => clearInterval(interval);
  // }, []);

  useEffect(() => {
    // if (doLog()) console.log("MessageInput MOUNTED");

    // Handle mounting logic
    if (!hasMounted.current) {
      hasMounted.current = true;
      // if (doLog()) console.log("MessageInput first mount, skipping focus set");
    } else if (isVisible && isDesktop) {
      if (doLog()) console.log("MessageInput is mounted & became visible, setting focus");
      setTimeout(() => {
        inputRef.current?.focus();
      }, 200);
    }

    return () => {
      // if (doLog()) console.log("MessageInput UNMOUNTED");
    };
  }, [isVisible]);

  const handleSend = (e) => {
    e.preventDefault();
    if (input.trim()) {
      const typingTimeMs = typingStartTime ? Date.now() - typingStartTime : 0;
      if (doLog()) console.log("handleSend", input, "typingTimeMs", typingTimeMs);
      onSubmit(input, typingTimeMs);
      setInput("");
      setTypingStartTime(null);
    }
  };

  const uiIsRtl = uiLangIsRtl();
  const targetLangIsRtl = localStorageGet(STORAGE_KEYS.targetLangIsRtl);
  const inputIsRtl = inActivity ? targetLangIsRtl : uiIsRtl;
  // if (doLog())
  //   console.log("inputIsRtl", inputIsRtl, inActivity, targetLangIsRtl, uiIsRtl);

  return (
    isVisible && (
      <form onSubmit={handleSend} className="flex space-x-2 items-end">
        <textarea
          ref={inputRef}
          value={input}
          rows="1"
          autoCorrect="off"
          spellCheck="false"
          autoCapitalize="off"
          autoComplete="off"
          onInput={(e) => {
            e.target.style.height = "auto";
            e.target.style.height = e.target.scrollHeight + "px";
            const newValue = e.target.value;
            if (newValue && !typingStartTime) {
              setTypingStartTime(Date.now());
            }
            setInput(newValue);
          }}
          onTouchMove={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSend(e);
            }
          }}
          placeholder={placeholder ?? strings.placeholder ?? "Type a message..."}
          className={`flex-1 h-auto w-full rounded-md border border-input bg-white px-2 py-2 text-md leading-snug shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 overflow-y-hidden ${
            inputIsRtl ? "text-right" : "text-left"
          }`}
          onFocus={() => {
            if (doLog()) console.log("onFocus MessageInput");
            if (onFocus) onFocus();
          }}
          onBlur={() => {
            if (doLog()) console.log("onBlur MessageInput");
            if (onBlur) onBlur();
          }}
          dir={inputIsRtl ? "rtl" : "ltr"}
        />
        {/* {emphasize && (
          <div
            className={`text-4xl transition-opacity duration-500 ${
              visible ? "opacity-100" : "opacity-0"
            }`}
          >
            👇
          </div>
        )} */}
        <Button
          type="submit"
          variant="taalia_std"
          onMouseDown={handleSend}
          onTouchStart={handleSend}
        >
          {strings.submit}
        </Button>
      </form>
    )
  );
}
